import { VITAL_SIGN_TYPES } from '@constants/fhir';

import { useConversion } from '@hooks/useConversion';
import { useHealthChart } from '@hooks/useHealthChart';

import { convertUnit } from '@utils/conversion';

import type { ParameterBoundary } from 'generated/types';
import { type ReferenceRangeRiskLevel } from 'generated/types';

export function useMetricWithinRange() {
  const { code, referenceRangeMap } = useHealthChart();
  const {
    state: { conversion },
  } = useConversion();
  const { precision, unit } = VITAL_SIGN_TYPES[code];

  function compareValues({
    bound,
    inclusive = true,
    type,
    value,
  }: {
    /** Lower or upper limit of ref range */
    bound: number;
    /** Is the limit inclusive? If T, >= or <=. If F, > or < */
    inclusive?: boolean;
    /** Lower or upper limit */
    type: 'lower' | 'upper';
    /** Metric value */
    value: number;
  }): boolean {
    switch (type) {
      case 'lower':
        return inclusive ? value >= bound : value > bound;
      case 'upper':
        return inclusive ? value <= bound : value < bound;
    }
  }

  function isWithinRange({
    value,
    lower_bound,
    upper_bound,
  }: {
    /** Metric value */
    value: number;
    /* Lower limit of ref range */
    lower_bound?: ParameterBoundary | null;
    /** Upper limit of ref range */
    upper_bound?: ParameterBoundary | null;
  }) {
    const low = lower_bound
      ? Number(
          convertUnit(
            lower_bound.value,
            unit,
            conversion[code] ? 'CON' : 'SI',
            precision,
            code
          )
        )
      : 0;
    const high = upper_bound
      ? Number(
          convertUnit(
            upper_bound.value,
            unit,
            conversion[code] ? 'CON' : 'SI',
            precision,
            code
          )
        )
      : Infinity;

    const lowerBoundCheck = compareValues({
      bound: low,
      inclusive: lower_bound?.inclusive ?? true,
      type: 'lower',
      value,
    });
    const upperBoundCheck = compareValues({
      bound: high,
      inclusive: upper_bound?.inclusive ?? true,
      type: 'upper',
      value,
    });

    return Boolean(lowerBoundCheck && upperBoundCheck);
  }

  const determineMetricRange = (
    value: number,
    code?: string
  ): ReferenceRangeRiskLevel | undefined => {
    const riskMap = referenceRangeMap;
    for (const riskLevel of riskMap.keys()) {
      const risk = riskMap.get(riskLevel);
      const parameter = risk?.parameters.find((param) => {
        return param.code === code || !param.code;
      });
      if (!parameter) {
        continue;
      }
      const { lower_bound, upper_bound } = parameter;

      const _isWithinRange = isWithinRange({
        value,
        lower_bound,
        upper_bound,
      });
      if (_isWithinRange) {
        return riskLevel;
      }
    }
    return undefined;
  };

  return {
    determineMetricRange,
  };
}
